import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Button,
  Text,
  VStack,
  Box,
} from '@chakra-ui/react';
import { useRef } from 'react';

interface BulkDeleteConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemCount: number;
  isLoading?: boolean;
  cascadeMessage?: string;
}

const BulkDeleteConfirmDialog: React.FC<BulkDeleteConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemCount,
  isLoading = false,
  cascadeMessage,
}) => {
  // AlertDialog needs `leastDestructiveRef` for focus; point it at the close
  // X (project pattern: dismiss via top-right close, no Cancel button).
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Theme colors
  const bgColor = 'bg.surface';
  const textColor = 'text.primary';
  const subtextColor = 'text.secondary';
  const warningBg = 'bg.warning';
  const warningBorder = 'border.warning';

  const defaultCascadeMessage =
    'These items will be moved to trash and permanently deleted after 30 days.';

  const itemText = itemCount === 1 ? 'item' : 'items';

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={closeButtonRef}
      // Gate every dismissal path (Esc, overlay click, close-X) while the
      // delete loop runs: Chakra's default closeOnEsc fired onClose mid-loop,
      // detaching a still-running chunked delete with no visible indicator
      // (PR #507 review).
      onClose={isLoading ? () => undefined : onClose}
      closeOnEsc={!isLoading}
      closeOnOverlayClick={!isLoading}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent bg={bgColor} color={textColor}>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Move {itemCount} {itemText} to trash?
          </AlertDialogHeader>

          <AlertDialogCloseButton ref={closeButtonRef} isDisabled={isLoading} />

          <AlertDialogBody>
            <VStack spacing={4} align="stretch">
              <Text>
                <Text as="span" fontWeight="bold">
                  {itemCount} {itemText}
                </Text>{' '}
                will be moved to trash.
              </Text>

              {/* Warning callout — bg.warning + border.warning + text.warning
                  is the calibrated trio for warning surfaces. */}
              <Box
                fontSize="sm"
                color="text.warning"
                bg={warningBg}
                p={3}
                borderRadius="md"
                border="1px"
                borderColor={warningBorder}
              >
                <Text>{cascadeMessage || defaultCascadeMessage}</Text>
              </Box>

              <Text fontSize="xs" color={subtextColor} fontStyle="italic">
                Large selections are processed in batches and may take a while. If some deletions
                fail, you&apos;ll be notified which ones succeeded and which failed.
              </Text>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              variant="warning"
              onClick={onConfirm}
              isLoading={isLoading}
              loadingText={`Moving ${itemCount} ${itemText}...`}
            >
              Move to Trash
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default BulkDeleteConfirmDialog;
